import type { ApiDocType } from "@src/api/stock/document/docTypes";
import type { ApiDivision } from "@src/api/dictionaries/divisions/types";
import type { ApiMeasure } from "@src/api/dictionaries/measures";
import type { OMPSearchString } from "@src/api/types/OmpApiTypes";
import type { DepotCardType } from "@src/types/business/depotCardType";
import type { ApiProductionKind } from "../productionKinds";
import type { StockObj, StockObjExtends } from "@src/api/stock/stockObjs";

/**
 * Складская карточка учёта ТМЦ в цехе (УП) — контракт OMP. \
 * Остатки и движения по номенклатуре в разрезе подразделения/участка.
 */
export interface ApiWsDepotCard {
    Code: number;
    /** Номер карточки */
    Num: string;
    StockobjCode: number;
    DivisionCode: number;
    SectionCode: number;
    ProductionTypeCode: number;
    MeasCode: number;
    /** Текущий остаток */
    Remainder: number;
    /** Признак партионного учёта на карточке */
    IsLotMoving: boolean;
    LastMove?: {
        DocDate: string;
        DocNum: string;
        DocType: number;
        MoveDate: string;
    };
}

/** Фильтр `stock/wsDepotcards.loadByFilter`. */
export interface ApiWsDepotCardFilter {
    Codes?: number[];
    DivisionCode?: number;
    Num?: OMPSearchString;
    StockobjSign?: OMPSearchString;
    StockobjName?: OMPSearchString;
    StockobjNomNum?: OMPSearchString;
    StockobjDesc?: OMPSearchString;
    /** Запросить блок `LastMove` в ответе */
    GetLastMove?: boolean;
}

/** Карточка УП с опциональными join-полями. */
export interface WsDepotCard extends ApiWsDepotCard, Partial<WsDepotCardExtends> {}

/** Связи для {@link wsDepotCardsServices.load}. */
export interface WsDepotCardExtends {
    Division: ApiDivision;
    /** Участок как производственное подразделение (`storeDivisions` + `SectionCodes`); `null`, если не задан. */
    DivisionSection: ApiDivision | null;
    Stockobj: StockObj;
    Measure: ApiMeasure;
    ProductionType: ApiProductionKind;
}

/** Алиас фильтра карточек УП. */
export type WsDepotCardFilter = ApiWsDepotCardFilter;

/** Направление количества в движении по карточке. */
export enum MoveQtyType {
    /** Расход */
    Leave = -1,
    /** Не классифицировано как приход/расход */
    Unknown = 0,
    /** Приход */
    Arrival = 1,
}

/** Движение по карточке СУ или УП — контракт OMP. */
export interface ApiDepotCardMovement {
    DepotCardType: DepotCardType;
    DepotCardCode: number;
    MoveDate: string;
    DocDate: string;
    DocNum: string;
    DocTypeCode: number;
    /**
     * Тип движения: при {@link MoveQtyType.Leave} `Qty` — расход, \
     * при {@link MoveQtyType.Arrival} — приход.
     */
    QtyType: MoveQtyType;
    Qty: number;
}

/** Фильтр загрузки движений по одной карточке. */
export interface ApiDepotCardMovementsFilter {
    DepotCardType: DepotCardType;
    DepotCardCode: number;
}

/** Дополнение движения расшифровкой типа документа. */
export interface DepotCardMovementExtends {
    DocType: ApiDocType;
}

/** Движение с опциональной расшифровкой типа документа. */
export type DepotCardMovement = ApiDepotCardMovement & Partial<DepotCardMovementExtends>;

export type { StockObjExtends };
